// src/components/Nav.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import CreatePostModal from './CreatePostModal';
import config from '../config';
import './Nav.css';

export default function Nav({ onUploadSuccess }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, API_URL } = useAuthContext();
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Add menu for + button
    const [addMenuOpen, setAddMenuOpen] = useState(false);

    const [uploading, setUploading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState(null);

    // Modal state
    const [createPostOpen, setCreatePostOpen] = useState(false);

    const dropdownRef = useRef(null);
    const addMenuRef = useRef(null);
    const fileInputRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
            if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
                setAddMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    const getInitials = () => {
        if (!user) return '?';
        const first = user.first_name || '';
        const last = user.last_name || '';
        if (first && last) {
            return `${first[0]}${last[0]}`.toUpperCase();
        }
        return (user.username || user.email || '?')[0].toUpperCase();
    };

    // New handlers
    const handleAddClick = () => {
        setAddMenuOpen(!addMenuOpen);
    };

    const handleCreatePostClick = () => {
        setAddMenuOpen(false);
        setCreatePostOpen(true);
    };

    const handleUploadGpxClick = () => {
        setAddMenuOpen(false);
        fileInputRef.current?.click();
    };

    const handlePostSuccess = (post) => {
        setUploadMessage({
            type: 'success',
            text: 'Post created!'
        });
        if (onUploadSuccess) onUploadSuccess();
        setTimeout(() => setUploadMessage(null), 3000);
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file || uploading) return;

        event.target.value = '';

        setUploading(true);
        setUploadMessage(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/api/activities/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setUploadMessage({
                    type: 'success',
                    text: `Activity "${data.activity.name}" uploaded!`
                });
                if (onUploadSuccess) {
                    onUploadSuccess(data.activity);
                }
                setTimeout(() => setUploadMessage(null), 3000);
            } else {
                setUploadMessage({
                    type: 'error',
                    text: data.error || 'Failed to upload activity'
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadMessage({
                type: 'error',
                text: 'Network error. Please try again.'
            });
        } finally {
            setUploading(false);
        }
    };

    const isActive = (path) => location.pathname === path;

    return (
        <>
            <header className="nav-header">
                <div className="nav-content">
                    {/* Brand */}
                    <div className="nav-brand">
                        <h1 className="nav-title">{config.siteTitle}</h1>
                        <nav className="nav-links desktop-only">
                            <Link
                                to="/dashboard"
                                className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
                            >
                                Feed
                            </Link>
                            <Link
                                to="/activities"
                                className={`nav-link ${isActive('/activities') ? 'active' : ''}`}
                            >
                                Activities
                            </Link>
                            <Link
                                to="/rank"
                                className={`nav-link ${isActive('/rank') ? 'active' : ''}`}
                            >
                                Compare
                            </Link>
                            <Link
                                to="/discuss"
                                className={`nav-link ${isActive('/discuss') ? 'active' : ''}`}
                            >
                                Discuss
                            </Link>
                        </nav>
                    </div>

                    {/* Nav Actions */}
                    <div className="nav-actions">

                        {/* New Add Button & Menu */}
                        <div className="add-menu-container" ref={addMenuRef}>
                            <button
                                className={`add-button ${addMenuOpen ? 'active' : ''}`}
                                onClick={handleAddClick}
                                disabled={uploading}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </button>

                            {addMenuOpen && (
                                <div className="add-dropdown">
                                    <button className="dropdown-item" onClick={handleCreatePostClick}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                        Create Post
                                    </button>
                                    <button className="dropdown-item" onClick={handleUploadGpxClick}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>
                                        Upload GPX
                                    </button>
                                </div>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".gpx"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />

                        {/* Avatar */}
                        <div className="avatar-container" ref={dropdownRef}>
                            <button
                                className="avatar-button"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                            >
                                <span className="avatar-initials">{getInitials()}</span>
                            </button>

                            {dropdownOpen && (
                                <div className="avatar-dropdown">
                                    <div className="dropdown-user-info">
                                        <span className="dropdown-name">
                                            {user?.first_name && user?.last_name
                                                ? `${user.first_name} ${user.last_name}`
                                                : user?.username}
                                        </span>
                                        <span className="dropdown-email">{user?.email}</span>
                                    </div>
                                    <div className="dropdown-divider"></div>
                                    <button className="dropdown-item logout-item" onClick={handleLogout}>
                                        <svg
                                            className="logout-icon"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                            <polyline points="16 17 21 12 16 7" />
                                            <line x1="21" y1="12" x2="9" y2="12" />
                                        </svg>
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {uploadMessage && (
                    <div className={`nav-toast ${uploadMessage.type}`}>
                        {uploadMessage.text}
                    </div>
                )}
            </header>

            {/* Modals */}
            <CreatePostModal
                isOpen={createPostOpen}
                onClose={() => setCreatePostOpen(false)}
                onSuccess={handlePostSuccess}
            />

            {/* Mobile Bottom Nav */}
            <nav className="mobile-bottom-nav">
                <Link
                    to="/dashboard"
                    className={`bottom-nav-item ${isActive('/dashboard') ? 'active' : ''}`}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                    <span>Feed</span>
                </Link>
                <Link
                    to="/activities"
                    className={`bottom-nav-item ${isActive('/activities') ? 'active' : ''}`}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span>Activities</span>
                </Link>

                <Link
                    to="/rank"
                    className={`bottom-nav-item ${isActive('/rank') ? 'active' : ''}`}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="20" x2="18" y2="10" />
                        <line x1="12" y1="20" x2="12" y2="4" />
                        <line x1="6" y1="20" x2="6" y2="14" />
                    </svg>
                    <span>Compare</span>
                </Link>
                <Link
                    to="/discuss"
                    className={`bottom-nav-item ${isActive('/discuss') ? 'active' : ''}`}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                    </svg>
                    <span>Discuss</span>
                </Link>
            </nav>
        </>
    );
}
